// ────────────────────────────────────────────────────────────
// GLOBAL WELD SESSION CHIP
// ────────────────────────────────────────────────────────────
// A compact topbar control, shared across /mi and /deal-desk, showing
// live Weld recorder-session state and a paste popover to refresh it —
// so you never have to trigger a blocked run just to reach the paste
// box. The skip-trace blocked-run banner keeps its own inline version.
//
// Status: GET /api/agent/session/weld (proxied) → weld_session
//   green  "Weld · 2h"      present, fresh
//   amber  "Weld · stale"   past the soft cap / needs refresh
//   red    "Weld · expired" absent
// Refresh: POST the pasted Cookie-Editor export through the same proxy
// the banner uses; the agent live-validates and reports validated /
// login_state. On success the chip flips green immediately.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  agentApi, readWeldSession, readValidated, readLoginState, formatAge,
  type WeldSession, type AgentResponse,
} from '../services/agentApi';

const POLL_MS = 3 * 60 * 1000;

type Health = 'loading' | 'green' | 'amber' | 'red' | 'unknown';

function healthOf(s: WeldSession | null): Health {
  if (!s) return 'unknown';
  if (!s.present) return 'red';
  if (s.stale || s.needsRefresh) return 'amber';
  return 'green';
}

function label(h: Health, s: WeldSession | null): string {
  switch (h) {
    case 'green': return `Weld · ${s?.age ?? (s?.ageSeconds != null ? formatAge(s.ageSeconds) : 'ok')}`;
    case 'amber': return 'Weld · stale';
    case 'red':   return 'Weld · expired';
    case 'loading': return 'Weld · …';
    default:      return 'Weld · ?';
  }
}

export function WeldSessionChip() {
  const [session, setSession] = useState<WeldSession | null>(null);
  const [health, setHealth] = useState<Health>('loading');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const poll = useCallback(async () => {
    try {
      const r = await agentApi.getWeldSessionStatus();
      const s = readWeldSession(r);
      setSession(s);
      setHealth(healthOf(s));
    } catch {
      setHealth('unknown');   // agent unreachable — chip shows "?", not a crash
    }
  }, []);

  useEffect(() => {
    void poll();
    const t = window.setInterval(() => void poll(), POLL_MS);
    return () => window.clearInterval(t);
  }, [poll]);

  // Refetch when the popover opens so the shown age is current.
  useEffect(() => { if (open) void poll(); }, [open, poll]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const onRefresh = async () => {
    const json = draft.trim();
    if (!json || refreshing) return;
    setRefreshing(true);
    setResult(null);
    try {
      const r: AgentResponse = await agentApi.refreshWeldSession(json);
      const s = readWeldSession(r);
      if (s) { setSession(s); setHealth(healthOf(s)); }
      const validated = readValidated(r);
      const login = readLoginState(r);
      if (validated) {
        setResult({ ok: true, msg: `Session validated${login ? ` · ${login}` : ''}.` });
        setDraft('');
      } else {
        setResult({ ok: false, msg: `Not validated${login ? ` · login state: ${login}` : ''}. Re-export and paste again.` });
      }
    } catch (e) {
      setResult({ ok: false, msg: (e as Error).message });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="ts-weld" ref={rootRef}>
      <button
        className={`ts-weld-chip ${health}`}
        onClick={() => setOpen(o => !o)}
        title="Weld recorder session — click to refresh"
      >
        <span className="ts-weld-dot" />
        <span className="ts-weld-label">{label(health, session)}</span>
      </button>

      {open && (
        <div className="ts-weld-pop" onClick={e => e.stopPropagation()}>
          <div className="ts-weld-pop-head">
            <span className={`ts-weld-dot ${health}`} />
            <strong>Weld recorder session</strong>
          </div>
          <div className="ts-weld-meta">
            <div><span className="k">State</span><span className="v">{
              health === 'green' ? 'Fresh' : health === 'amber' ? 'Stale — refresh soon' :
              health === 'red' ? 'Expired / absent' : health === 'loading' ? 'Checking…' : 'Agent unreachable'
            }</span></div>
            <div><span className="k">Age</span><span className="v">{session?.age ?? (session?.ageSeconds != null ? formatAge(session.ageSeconds) : '—')}</span></div>
            <div><span className="k">Last refreshed</span><span className="v">{session?.lastModified ? new Date(session.lastModified).toLocaleString() : '—'}</span></div>
            {session?.warning && <div className="ts-weld-warn">{session.warning}</div>}
          </div>

          <div className="ts-weld-refresh">
            <div className="ts-weld-refresh-label">Paste Weld session JSON (Cookie-Editor → Export)</div>
            <textarea
              className="ts-weld-textarea"
              placeholder='[{"name":"...","value":"...","domain":"recording.weld.gov", ...}]'
              value={draft}
              onChange={e => setDraft(e.target.value)}
              spellCheck={false}
            />
            {result && (
              <div className={`ts-weld-result ${result.ok ? 'ok' : 'err'}`}>{result.msg}</div>
            )}
            <button className="ts-weld-btn" onClick={() => void onRefresh()} disabled={refreshing || !draft.trim()}>
              {refreshing ? 'Validating…' : 'Refresh session'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
